/**
 * ComplianceTab — iter83 Compliance v1 · iter85.1 Compliance Ops IA
 *
 * scope="platform" (rendered in Platform Console → Compliance Ops):
 *   Privacy request queue + execute, suppression list, Data Dictionary
 *   (with platform/consumer layer badges), Audit Log, Export User Data.
 *
 * scope="site" (rendered at /admin → Compliance, all roles):
 *   Tenant-scoped privacy request intake only — collect a player /
 *   rivalry-admin request and push it to the platform queue.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldCheck, FileWarning, BookOpen, ScrollText, UserSearch,
    Loader2, Download, ChevronDown, ChevronRight, AlertTriangle, Search,
} from "lucide-react";
import PrivacyRequestsSection from "@/components/site-admin/compliance/PrivacyRequestsSection";
import SuppressionSection from "@/components/site-admin/compliance/SuppressionSection";
import TenantPrivacySection from "@/components/site-admin/compliance/TenantPrivacySection";

export default function ComplianceTab({ scope = "site" }) {
    const isPlatform = scope === "platform";
    if (!isPlatform) return <TenantPlaceholder />;

    return (
        <div className="space-y-5" data-testid="compliance-tab-platform">
            <header>
                <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6 text-cyber" /> Compliance Ops
                </h2>
                <p className="text-xs text-zinc-500 mt-1 max-w-3xl leading-relaxed">
                    Privacy request execution, suppression, data dictionary, audit trail, and exports.
                    The vendor & contract registry moved to Canon → Obligations (view) and Stewardship → Vendors (govern).
                    Commons Admin scope only.
                </p>
            </header>

            <PrivacyRequestsSection />
            <SuppressionSection />
            <DataDictionarySection />
            <AuditLogSection />
            <UserExportSection />
        </div>
    );
}

// ---- Tenant scope (placeholder, escalate to platform) ---------------

function TenantPlaceholder() {
    return (
        <div className="space-y-4" data-testid="compliance-tab-site">
            <div>
                <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                    <ShieldCheck className="w-6 h-6 text-cyber" /> Compliance
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                    Privacy requests for your players and rivalry admins.
                </p>
            </div>
            <TenantPrivacySection />
            <div className="brutal-card-light p-4 flex items-start gap-3" data-testid="compliance-ownership-note">
                <FileWarning className="w-5 h-5 text-cyber shrink-0 mt-0.5" />
                <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-400 font-bold mb-1">Ownership</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                        <strong className="text-volt">Commons Admin owns execution</strong> — anonymization, suppression,
                        the full data dictionary, and audit tooling live in the Commons Console under{" "}
                        <strong className="text-white">Compliance Ops</strong>. Your job here is intake: collect the
                        request from your user and push it to the queue above.
                    </p>
                </div>
            </div>
        </div>
    );
}

// ---- Data Dictionary ------------------------------------------------

function DataDictionarySection() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.complianceDataDictionary();
                if (!cancelled) setData(r);
            } catch (err) {
                if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load data dictionary");
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <section className="brutal-card" data-testid="compliance-data-dictionary-section">
            <SectionHeader
                icon={BookOpen}
                title="Data Dictionary"
                subtitle="Every collection on the platform, its purpose, tenant scope, and PII classification."
                open={open}
                onToggle={() => setOpen((v) => !v)}
                testid="compliance-data-dictionary-toggle"
                pillRight={data ? `${data.totals.collections} collections` : null}
            />
            {open && (
                <div className="p-4 pt-0">
                    {loading ? (
                        <Spinner label="Loading dictionary…" />
                    ) : !data ? (
                        <EmptyHint text="Unable to load dictionary." />
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
                                {Object.entries(data.totals.pii).map(([level, count]) => (
                                    <div
                                        key={level}
                                        data-testid={`pii-pill-${level}`}
                                        className="brutal-card-light p-2 text-center"
                                    >
                                        <div className={`text-[10px] uppercase tracking-wider font-bold ${PII_COLOR[level]}`}>
                                            {level} PII
                                        </div>
                                        <div className="text-lg font-display font-extrabold">{count}</div>
                                    </div>
                                ))}
                                {Object.entries(data.totals.layers || {}).map(([layer, count]) => (
                                    <div
                                        key={layer}
                                        data-testid={`layer-pill-${layer}`}
                                        className="brutal-card-light p-2 text-center"
                                    >
                                        <div className={`text-[10px] uppercase tracking-wider font-bold ${LAYER_COLOR[layer]}`}>
                                            {layer} layer
                                        </div>
                                        <div className="text-lg font-display font-extrabold">{count}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="data-dictionary-table">
                                    <thead className="bg-[#0a0a0a] border-b-2 border-[#333]">
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                                            <th className="px-3 py-2 font-bold">Collection</th>
                                            <th className="px-3 py-2 font-bold">Layer</th>
                                            <th className="px-3 py-2 font-bold">Purpose</th>
                                            <th className="px-3 py-2 font-bold">Tenant scope</th>
                                            <th className="px-3 py-2 font-bold">PII</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.entries.map((e) => (
                                            <tr
                                                key={e.collection}
                                                className="border-b border-[#222]"
                                                data-testid={`data-dictionary-row-${e.collection}`}
                                            >
                                                <td className="px-3 py-2 font-mono text-white">{e.collection}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`pill text-[9px] font-bold uppercase ${LAYER_COLOR[e.layer] || "text-zinc-400"} border-current/40`} data-testid={`layer-badge-${e.collection}`}>
                                                        {e.layer || "—"}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-zinc-300">{e.purpose}</td>
                                                <td className="px-3 py-2 font-mono text-zinc-400">{e.tenant_scope}</td>
                                                <td className={`px-3 py-2 font-bold uppercase tracking-wider text-[10px] ${PII_COLOR[e.pii]}`}>
                                                    {e.pii}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}

const PII_COLOR = {
    high: "text-red-400",
    medium: "text-amber-400",
    low: "text-zinc-300",
    none: "text-zinc-500",
};

const LAYER_COLOR = {
    platform: "text-volt",
    consumer: "text-cyber",
};

// ---- Audit Log ------------------------------------------------------

function AuditLogSection() {
    const [filters, setFilters] = useState({ tenant_id: "", actor_email: "", action: "" });
    const [rows, setRows] = useState([]);
    const [nextBefore, setNextBefore] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(true);

    const search = useCallback(async (cursor = null) => {
        setLoading(true);
        try {
            const params = { ...filters, limit: 50 };
            if (cursor) params.before = cursor;
            const r = await api.complianceAuditLog(params);
            setRows((prev) => (cursor ? [...prev, ...r.rows] : r.rows));
            setNextBefore(r.next_before);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to query audit log");
        }
        setLoading(false);
    }, [filters]);

    return (
        <section className="brutal-card" data-testid="compliance-audit-log-section">
            <SectionHeader
                icon={ScrollText}
                title="Audit Log"
                subtitle="Filter by tenant, actor email, or action. Cursor pagination on created_at."
                open={open}
                onToggle={() => setOpen((v) => !v)}
                testid="compliance-audit-log-toggle"
            />
            {open && (
                <div className="p-4 pt-0 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input
                            data-testid="audit-filter-tenant-id"
                            value={filters.tenant_id}
                            onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })}
                            placeholder="tenant_id (UUID)"
                            className="brutal-input text-xs font-mono"
                        />
                        <input
                            data-testid="audit-filter-actor-email"
                            value={filters.actor_email}
                            onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
                            placeholder="actor email (substring)"
                            className="brutal-input text-xs"
                        />
                        <input
                            data-testid="audit-filter-action"
                            value={filters.action}
                            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                            placeholder="action (exact match)"
                            className="brutal-input text-xs"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => search(null)}
                        disabled={loading}
                        data-testid="audit-search-btn"
                        className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                    >
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        {loading ? "Searching…" : "Search"}
                    </button>

                    {rows.length === 0 && !loading ? (
                        <EmptyHint text="No results yet — set filters and Search." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="audit-log-table">
                                <thead className="bg-[#0a0a0a] border-b-2 border-[#333]">
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                                        <th className="px-3 py-2 font-bold">When</th>
                                        <th className="px-3 py-2 font-bold">Actor</th>
                                        <th className="px-3 py-2 font-bold">Tenant</th>
                                        <th className="px-3 py-2 font-bold">Action</th>
                                        <th className="px-3 py-2 font-bold">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr
                                            key={(row.id || "") + i}
                                            className="border-b border-[#222]"
                                            data-testid={`audit-row-${i}`}
                                        >
                                            <td className="px-3 py-2 font-mono text-zinc-400 whitespace-nowrap">
                                                {fmtTs(row.created_at)}
                                            </td>
                                            <td className="px-3 py-2 text-zinc-300">{row.actor_email}</td>
                                            <td className="px-3 py-2 font-mono text-zinc-500">
                                                {row.tenant_id ? row.tenant_id.slice(0, 8) + "…" : "—"}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-cyber">{row.action}</td>
                                            <td className="px-3 py-2 text-zinc-400">{row.reason || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {nextBefore && (
                        <button
                            type="button"
                            onClick={() => search(nextBefore)}
                            disabled={loading}
                            data-testid="audit-load-more"
                            className="brutal-btn-ghost text-xs py-2 w-full"
                        >
                            Load more…
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

function fmtTs(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
    } catch {
        return iso;
    }
}

// ---- Export User ----------------------------------------------------

function UserExportSection() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(true);

    const run = async () => {
        const e = email.trim().toLowerCase();
        if (!e || !e.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const r = await api.complianceExportUser(e);
            setResult(r);
            toast.success(`Found ${r.found_total} document${r.found_total === 1 ? "" : "s"}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Export failed");
        }
        setBusy(false);
    };

    const download = () => {
        if (!result) return;
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const safeEmail = (result.email || "user").replace(/[^a-z0-9]+/gi, "_");
        a.download = `user-export-${safeEmail}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <section className="brutal-card" data-testid="compliance-user-export-section">
            <SectionHeader
                icon={UserSearch}
                title="Export User Data"
                subtitle="GDPR-style dump of every document referencing this email."
                open={open}
                onToggle={() => setOpen((v) => !v)}
                testid="compliance-user-export-toggle"
            />
            {open && (
                <div className="p-4 pt-0 space-y-3">
                    <div className="flex gap-2 items-stretch">
                        <input
                            data-testid="user-export-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            className="brutal-input text-xs flex-1"
                        />
                        <button
                            type="button"
                            onClick={run}
                            disabled={busy}
                            data-testid="user-export-run"
                            className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                            {busy ? "Exporting…" : "Export"}
                        </button>
                    </div>

                    {result && (
                        <div className="brutal-card-light p-3 space-y-2" data-testid="user-export-result">
                            <div className="flex justify-between items-center">
                                <p className="text-xs text-zinc-400">
                                    <strong className="text-white">{result.email}</strong> — {result.found_total} document{result.found_total === 1 ? "" : "s"} found
                                </p>
                                <button
                                    type="button"
                                    onClick={download}
                                    data-testid="user-export-download"
                                    className="brutal-btn-ghost text-xs py-1.5"
                                >
                                    <Download className="w-3 h-3" /> Download JSON
                                </button>
                            </div>

                            {result.found_total === 0 && (
                                <p className="text-xs text-zinc-500 flex items-center gap-1.5">
                                    <AlertTriangle className="w-3 h-3 text-amber-400" />
                                    No data found for this email. Either it's not registered or it's
                                    in a tenant outside the platform's visibility.
                                </p>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                {Object.entries(result.totals).map(([section, count]) => (
                                    <div
                                        key={section}
                                        data-testid={`user-export-section-${section}`}
                                        className={`p-2 border ${
                                            count > 0
                                                ? "border-cyber/40 bg-cyber/5"
                                                : "border-[#222] bg-transparent"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                                            {section}
                                        </div>
                                        <div className={`text-base font-display font-extrabold ${count > 0 ? "text-cyber" : "text-zinc-600"}`}>
                                            {count}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="text-[10px] text-zinc-600 leading-relaxed">
                        Read-only. Deletion is a separate workflow (not yet wired).
                        Password hashes and one-shot tokens are scrubbed from the dump.
                    </p>
                </div>
            )}
        </section>
    );
}

// ---- shared bits ----------------------------------------------------

function SectionHeader({ icon: Icon, title, subtitle, open, onToggle, testid, pillRight }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onToggle}
            className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#0a0a0a]/40 transition-colors"
        >
            {open ? <ChevronDown className="w-4 h-4 text-cyber shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
            <Icon className="w-5 h-5 text-cyber shrink-0" />
            <div className="flex-1">
                <h3 className="font-display font-extrabold text-base uppercase">{title}</h3>
                <p className="text-[11px] text-zinc-500">{subtitle}</p>
            </div>
            {pillRight && (
                <span className="pill text-[10px] font-bold uppercase border-[#333] text-zinc-400">
                    {pillRight}
                </span>
            )}
        </button>
    );
}

function Spinner({ label }) {
    return (
        <div className="flex items-center justify-center gap-2 p-6 text-zinc-400 text-xs">
            <Loader2 className="w-4 h-4 animate-spin" />
            {label}
        </div>
    );
}

function EmptyHint({ text }) {
    return (
        <p className="text-xs text-zinc-500 italic text-center p-4">
            {text}
        </p>
    );
}
